import {
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { type AgentThreadRecord } from "@/shared/domain/agentThread";

export interface UseAgentThreadParams {
  /**
   * Identifier of the agent thread to fetch. Required — the caller
   * must narrow ``threadId`` to a string (e.g. by branching on the
   * value from ``useParams``) before invoking this hook.
   */
  threadId: string;
}

/**
 * Fetches the full record for one agent thread.
 *
 * Suspends until the response is available and throws to the nearest
 * ``<ErrorBoundary>`` on failure, so callers can keep the rendering of
 * the loaded state free of ``isLoading`` / ``error`` branching. Unlike
 * :func:`useActiveAgentThread`, this hook does not poll: the polling lives inside
 * the interactive ``<AgentThread />`` rendered for threads the user owns,
 * and read-only threads don't change after load.
 *
 * The query key (``["thread", threadId]``) is distinct from the
 * sidebar's ``useActiveAgentThread`` key so a detail page mounted on the same screen
 * as the sidebar does not contend with it on cache invalidation.
 */
export function useAgentThread({
  threadId,
}: UseAgentThreadParams): UseSuspenseQueryResult<AgentThreadRecord, Error> {
  const { agentThreadService } = useDomainServices();
  return useSuspenseQuery<AgentThreadRecord, Error>({
    queryKey: ["agentThread", threadId],
    queryFn: ({ signal }) =>
      agentThreadService.getAgentThreadById(threadId, true, {
        abortSignal: signal,
      }),
  });
}
